import { DatasetItem, GridRow } from '../../../types/types';
import { formatString } from '../../../utils/formatString';
import { formatValue } from '../../../utils/managerUtils';
import { ResolvedPrintColumn } from './resolvePrintColumns';
import { formatTypedPrintValue } from './typedValueFormat';

/** Types whose formatting formatValue owns; a format string does not apply. */
const DATE_TYPES = ['date', 'time', 'dateTime'];

/**
 * True when formatValue would drop the column's format string on the floor.
 *
 * formatValue applies `format` only to float columns: for anything else it
 * computes the formatted value and then falls through without returning it, so
 * "%d ks" on an integer prints "5". That is a missing return rather than a
 * decision, and a printed report has to honour the unit the user asked for — the
 * whole point of offering "ks" and "kg" in the designer.
 *
 * Fixed here, in the print path, rather than in formatValue: that function also
 * feeds every grid and detail view in the manager, and changing what they render
 * is not part of this work.
 */
const formatIsIgnoredByFormatValue = (
  formatConfig: ResolvedPrintColumn['formatConfig']
): boolean => {
  const { type, format } = formatConfig;

  return (
    Boolean(format) && type !== 'float' && !DATE_TYPES.includes(type ?? '')
  );
};

/**
 * The shared grid formatter keeps only the first whitespace-delimited unit
 * token for floats. Report formats are user-editable and may contain an entire
 * suffix (for example "Kč / ks"), so restore that suffix from sprintf's raw
 * output while retaining the grid's localized numeric prefix.
 */
const preserveCompleteFloatSuffix = (
  row: GridRow,
  column: ResolvedPrintColumn,
  value: number | string,
  formatted: string
): string => {
  const { type, format, formatItem } = column.formatConfig;
  if (type !== 'float') return formatted;

  const rowValues = row as Record<string, unknown>;
  const appliedFormat =
    format ??
    (formatItem && rowValues[formatItem]
      ? String(rowValues[formatItem])
      : undefined);
  if (!appliedFormat) return formatted;

  const raw = String(formatString(appliedFormat, value)).trim();
  const rawSuffixIndex = raw.indexOf(' ');
  const formattedSuffixIndex = formatted.indexOf(' ');
  if (rawSuffixIndex < 0 || formattedSuffixIndex < 0) return formatted;

  return `${formatted.slice(0, formattedSuffixIndex)}${raw.slice(
    rawSuffixIndex
  )}`;
};

/**
 * Formats a cell with the very same formatter the grid and detail views use, so
 * a printed report cannot drift from what the user sees on screen — including
 * the %m default-currency substitution and per-row formatItem.
 */
export const formatPrintValue = (
  row: GridRow,
  column: ResolvedPrintColumn,
  locale = 'cs-CZ'
): string => {
  const value = (row as Record<string, unknown>)[column.name];

  if (value !== null && value !== undefined && typeof value !== 'object') {
    const typedValue = formatTypedPrintValue(
      value,
      column.formatConfig.type,
      column.formatConfig.format,
      locale
    );
    if (typedValue !== null) {
      return typedValue;
    }
  }

  if (
    formatIsIgnoredByFormatValue(column.formatConfig) &&
    value !== null &&
    value !== undefined &&
    typeof value !== 'object'
  ) {
    return String(
      formatString(
        column.formatConfig.format as string,
        value as number | string
      )
    );
  }

  const formatted = String(formatValue(row, column.formatConfig));
  if (
    value !== null &&
    value !== undefined &&
    typeof value !== 'object'
  ) {
    return preserveCompleteFloatSuffix(
      row,
      column,
      value as number | string,
      formatted
    );
  }

  return formatted;
};

/**
 * Formats a computed number (an aggregate) using a definition item's format, so
 * a footer total is rendered like the column it sums.
 */
export const formatPrintNumber = (
  value: number | undefined,
  item?: DatasetItem
): string => {
  if (value === undefined) {
    return '';
  }

  const name = item?.name ?? 'value';
  const formatConfig = {
    name,
    type: item?.type ?? 'float',
    format: item?.format,
  };
  const aggregateColumn: ResolvedPrintColumn = {
    name,
    label: item?.label ?? name,
    align: 'right',
    isNumeric: true,
    formatConfig,
  };

  // Keep aggregates on the exact same guarded print formatter as table cells:
  // this includes non-float formats and complete custom unit suffixes.
  return formatPrintValue(
    { [name]: value } as unknown as GridRow,
    aggregateColumn
  );
};
